import "./App.css";

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ChatLayout from "./pages/ChatLayout";
import Composer from "./pages/Composer";
import CheckoutView from "./components/CheckoutView";
import BottomSheet from "./ui/BottomSheet";
import ProductSheet from "./components/ui/ProductSheet";
import PresetModal from "./components/ui/PresetModal";
import DeliveryModal from "./components/ui/DeliveryModal";
import CatalogSheet from "./ui/CatalogSheet";

import { useChatSession } from "./chat/useChatSession";
import { isFavoriteProductByIds } from "./app/app.helpers";
import { createUiHandlers } from "./app/app.handlers";
import {
  bindAuthChanged,
  restoreAfterCheckoutReturn,
} from "./app/app.effects";
import { getMyAccountData } from "./api/api";
import featuredTabIcon from "./assets/catalog-featured.png";
import MiniCatalogSheet from "./components/MiniCatalogSheet";
import { trackEvent, updatePurchaseUserProperties } from "./services/amplitude";
import Onboarding from "./components/onboarding/Onboarding";
import BeerFinder from "./components/BeerFinder/BeerFinder";
import { mapBeerQuizToFilters } from "./utils/mapBeerQuizToFilters";
import { apiBeerFinder, apiGetMyBeerFinder } from "./api/beerFinder.api";
import { loginWithGoogle } from "./services/auth.service";



export default function App() {
  const { t } = useTranslation(["cart", "beerFinder"]);

  const {
    chatId,
    loadChat,
    startNewChat,
    resetSession,

    screen,
    setScreen,

    messagesWithTyping,
    ctas,
    loading,

    cartItems,
    cartSummary,

    send,
    onCtaClick,
    onAction,
    appendOrderConfirmation,
  } = useChatSession();

  const [preset, setPreset] = useState("dine_in");
  const [presetModalOpen, setPresetModalOpen] = useState(false);

  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);

  const [productOpen, setProductOpen] = useState(false);
  const [sheetProduct, setSheetProduct] = useState(null);
  const [productInitialTab, setProductInitialTab] = useState("info");
  const [productInitialOpenComment, setProductInitialOpenComment] = useState(false);

  const [catalogOpen, setCatalogOpen] = useState(false);

  const [miniCatalogOpen, setMiniCatalogOpen] = useState(false);
  const [miniCatalogTitle, setMiniCatalogTitle] = useState("");
  const [miniCatalogProducts, setMiniCatalogProducts] = useState([]);

  const [awaitingCheckout, setAwaitingCheckout] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const [favoriteIds, setFavoriteIds] = useState([]);
  const [favoriteItems, setFavoriteItems] = useState([]);
  const [authReady, setAuthReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [productOpenedFromCatalog, setProductOpenedFromCatalog] = useState(false);

  const location = useLocation();
  const nav = useNavigate();
  const { chatId: routeChatId } = useParams();

  const ignoreFavoritesRef = useRef(false);
  const loadedChatRef = useRef(null);
  const pendingNewChatRef = useRef(false);
  const checkoutRestoreRef = useRef(false);
  const [beerFinderOpen, setBeerFinderOpen] = useState(false);
  const [beerFinderMode, setBeerFinderMode] = useState("quiz");
  const [beerFinderSaved, setBeerFinderSaved] = useState(null);
  const [beerFinderProductOpen, setBeerFinderProductOpen] = useState(false);
  const [beerFinderProduct, setBeerFinderProduct] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(() => {
  return localStorage.getItem("chatshop_onboarding_seen") !== "true";
});


  useEffect(() => {
    const stateChatId = location.state?.openChatId;
    const nextChatId = String(stateChatId || routeChatId || "").trim();

    if (!nextChatId) return;
    if (loadedChatRef.current === nextChatId) return;

    loadedChatRef.current = nextChatId;

    (async () => {
      try {
        await loadChat(nextChatId);
        setScreen("chat");
      } catch (e) {
        console.error("open chat from route/state error:", e);
      } finally {
        if (stateChatId) {
          nav(`/chat/${nextChatId}`, { replace: true });
        }
      }
    })();
  }, [location.key, routeChatId, loadChat, nav, setScreen]);

  useEffect(() => {
    if (!pendingNewChatRef.current) return;
    if (location.pathname !== "/") return;

    pendingNewChatRef.current = false;
    loadedChatRef.current = null;
    startNewChat?.();
  }, [location.pathname, startNewChat]);

  useEffect(() => {
    const unsub = bindAuthChanged({
      resetSession,
      setIsLoggedIn,
      setAuthReady,
      setFavoriteIds,
      setFavoriteItems,
      setCartOpen,
      setProductOpen,
      setSheetProduct,
      setCatalogOpen,
      setPresetModalOpen,
      setDeliveryOpen,
      setAwaitingCheckout,
    });

    return () => unsub();
  }, [resetSession]);

  const handlers = createUiHandlers({
    chatId,
    cartItems,
    cartSummary,
    preset,
    checkoutLoading,
    setCheckoutLoading,
    setCartOpen,
    setProductOpen,
    setSheetProduct,
    setCatalogOpen,
    setScreen,
    setPresetModalOpen,
    setAwaitingCheckout,
    setDeliveryOpen,
    onAction,
    onCtaClick,
    startNewChat,
    loadChat,
    send,
    ctas,
    loading,
    favoriteIds,
    setFavoriteIds,
    favoriteItems,
    setFavoriteItems,
    authReady,
    isLoggedIn,
    cartOpen,
    productOpen,
    catalogOpen,
    setMiniCatalogOpen,
    miniCatalogOpen,
    setBeerFinderOpen,
    setBeerFinderMode,
    setMiniCatalogTitle,
    setMiniCatalogProducts,
  });

  useEffect(() => {
    restoreAfterCheckoutReturn({
      cartItemsLength: cartItems.length,
      setCheckoutLoading,
      setPresetModalOpen,
      setDeliveryOpen,
      setAwaitingCheckout,
      setScreen,
      setCartOpen,
    });

    function onPageShow() {
      restoreAfterCheckoutReturn({
        cartItemsLength: cartItems.length,
        setCheckoutLoading,
        setPresetModalOpen,
        setDeliveryOpen,
        setAwaitingCheckout,
        setScreen,
        setCartOpen,
      });
    }

    window.addEventListener("pageshow", onPageShow);

    return () => {
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [cartItems.length, setScreen]);

  useEffect(() => {
    async function restorePaidOrderToChat() {
      const isCheckoutSuccess = !!location.state?.checkoutSuccess;

      if (!isCheckoutSuccess || checkoutRestoreRef.current) return;

      checkoutRestoreRef.current = true;

      try {
        const targetChatId =
          location.state?.openChatId ||
          sessionStorage.getItem("checkout_chat_id");

        if (targetChatId) {
          await loadChat(targetChatId);
        }

        setScreen("chat");

        const data = await getMyAccountData();
        const purchases = Array.isArray(data?.purchases) ? data.purchases : [];

        const checkoutStartedAt = Number(
  sessionStorage.getItem("checkout_started_at") || 0
);

const paidOrders = purchases.filter((o) => {
  const status = String(o?.status || "").toLowerCase();

  const orderTime = new Date(
    o?.created_at || o?.date || 0
  ).getTime();

  return (
    status === "paid" &&
    orderTime >= checkoutStartedAt
  );
});

        const latestOrder = paidOrders.sort((a, b) => {
          const da = new Date(a?.date || a?.created_at || 0).getTime();
          const db = new Date(b?.date || b?.created_at || 0).getTime();
          return db - da;
        })[0];

        if (latestOrder && targetChatId) {
          const items = Array.isArray(latestOrder.items) ? latestOrder.items : [];

          const itemsCount = items.reduce((sum, item) => {
            return sum + Number(item.qty || item.quantity || 1);
          }, 0);

          trackEvent("purchase", {
            order_id: latestOrder.id || latestOrder.receipt_number || null,
            receipt_number: latestOrder.receipt_number || null,
            items_count: itemsCount,

            product_ids: items
              .map((item) => item.id || item.product_id || item.variant_id)
              .filter(Boolean),

            product_names: items
              .map((item) => item.title || item.name || null)
              .filter(Boolean),

            items: items.map((item) => ({
              id: item.id || item.product_id || item.variant_id || null,
              name: item.title || item.name || null,
              qty: Number(item.qty || item.quantity || 1),
              price: Number(item.price || 0),
            })),

            total: Number(latestOrder.total || 0),
            currency: "UAH",
            chat_id: targetChatId,
          });

          updatePurchaseUserProperties({
            itemsCount,
          });

          await appendOrderConfirmation(latestOrder, targetChatId);
        }
      } catch (e) {
        console.error("restorePaidOrderToChat error:", e);
      } finally {
  sessionStorage.removeItem("checkout_return_pending");
  sessionStorage.removeItem("checkout_chat_id");
  sessionStorage.removeItem("checkout_started_at");
}
    }

    restorePaidOrderToChat();
  }, [location.state, loadChat, setScreen, appendOrderConfirmation]);

  useEffect(() => {
    if (!authReady) return;

    ignoreFavoritesRef.current = false;
    handlers.loadFavoritesSafe({ ignoreRef: ignoreFavoritesRef });

    return () => {
      ignoreFavoritesRef.current = true;
    };
  }, [authReady, isLoggedIn]);

  useEffect(() => {
    if (!authReady || !isLoggedIn) return;

    const rawProduct = sessionStorage.getItem("pending_review_product");
    if (!rawProduct) return;

    try {
      const parsedProduct = JSON.parse(rawProduct);
      const savedTab = sessionStorage.getItem("pending_review_tab") || "reviews";
      const savedOpenComment =
        sessionStorage.getItem("pending_review_open_comment") === "true";

      setSheetProduct(parsedProduct);
      setProductInitialTab(savedTab);
      setProductInitialOpenComment(savedOpenComment);
      setProductOpen(true);
    } catch (e) {
      console.error("restore pending product after login error:", e);
    } finally {
      sessionStorage.removeItem("pending_review_product");
      sessionStorage.removeItem("pending_review_tab");
      sessionStorage.removeItem("pending_review_open_comment");
    }
  }, [authReady, isLoggedIn]);

  useEffect(() => {
    if (!authReady || !isLoggedIn) {
      setBeerFinderSaved(null);
      return;
    }

    let cancelled = false;

    async function loadBeerFinderProfile() {
      try {
        const res = await apiGetMyBeerFinder();

        if (cancelled) return;

        if (res?.hasProfile) {
          setBeerFinderSaved(res);
        } else {
          setBeerFinderSaved(null);
        }
      } catch (e) {
        console.error("load beer finder profile error:", e);
        setBeerFinderSaved(null);
      }
    }

    loadBeerFinderProfile();

    return () => {
      cancelled = true;
    };
  }, [authReady, isLoggedIn]);

  const isFavorite = useMemo(
    () => isFavoriteProductByIds(sheetProduct, favoriteIds),
    [sheetProduct, favoriteIds]
  );

  const checkoutProps = handlers.buildCheckoutProps();

  function handleStartFreshChat() {
    loadedChatRef.current = null;

    if (location.pathname !== "/") {
      pendingNewChatRef.current = true;
      nav("/");
      return;
    }

    startNewChat?.();
  }

  if (screen === "checkout") {
    return (
      <CheckoutView
        {...checkoutProps}
        onBack={handlers.handleCheckoutBack}
      />
    );
  }



  return (
    <>
      <ChatLayout
        chatId={chatId}
        messages={messagesWithTyping}
        onBlockAction={handlers.handleAction}
        onMessageAction={handlers.handleMessageAction}
        onOpenChat={(id) => {
          if (!id) return;
          nav(`/chat/${id}`);
        }}
        onNewChat={handleStartFreshChat}
        onLogoClick={handleStartFreshChat}
        overlayOpen={
          catalogOpen ||
          miniCatalogOpen ||
          productOpen ||
          beerFinderProductOpen ||
          cartOpen ||
          presetModalOpen ||
          deliveryOpen ||
          beerFinderOpen
        }
        favoriteItems={favoriteItems}
        onOpenProduct={handlers.handleOpenProduct}
        onRemoveFavorite={handlers.handleToggleFavorite}
        footer={
          <Composer
            ctas={[

              beerFinderSaved
                ? {
                  id: "beer_finder_my",
                  label: t("beerFinder:cta_my_label"),
                  sublabel: t("beerFinder:cta_my_sub"),
                }
                : {
                  id: "beer_finder",
                  label: t("beerFinder:cta_start_label"),
                  sublabel: t("beerFinder:cta_start_sub"),
                },

              ...ctas,
            ]}
            loading={loading}
            onSend={send}
            onCtaClick={handlers.handleCtaClick}
            cartCount={cartSummary.itemsCount}
            onCartClick={handlers.openCart}
            onGridClick={handlers.handleOpenCatalog}
            cartOverlay={
              (productOpen ||
                catalogOpen ||
                miniCatalogOpen ||
                beerFinderProductOpen) &&
              !cartOpen
            }
            chatId={chatId}
          />
        }
      />

      <CatalogSheet
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        topOffset={60}
        onOpenProduct={(p) => {
          if (!p) return;

          setProductOpenedFromCatalog(true);
          setSheetProduct(p);
          setProductInitialTab("info");
          setProductInitialOpenComment(false);
          setProductOpen(true);
        }}
        onAction={handlers.handleAction}
        featuredTabIcon={featuredTabIcon}
      />

      <PresetModal
        open={presetModalOpen}
        value={preset}
        onClose={() => {
          setPresetModalOpen(false);
          setAwaitingCheckout(false);
        }}
        onConfirm={(nextPreset) => {
          setPreset(nextPreset);
          setPresetModalOpen(false);

          if (!awaitingCheckout) return;

          if (nextPreset === "delivery") {
            setDeliveryOpen(true);
            return;
          }

          setAwaitingCheckout(false);
          handlers.handleCheckoutToOdoo();
        }}
        isLoggedIn={isLoggedIn}
onLogin={async () => {
  try {
    await loginWithGoogle();
  } catch (e) {
    console.error("checkout login error:", e);
  }
}}
      />

      <DeliveryModal
        open={deliveryOpen}
        onClose={() => {
          setDeliveryOpen(false);
          setAwaitingCheckout(false);
        }}
        onSaved={() => {
          setDeliveryOpen(false);

          if (awaitingCheckout) {
            setAwaitingCheckout(false);
            handlers.handleCheckoutToOdoo();
          }
        }}
      />

      <BottomSheet
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        title={t("my_cart")}
        topOffset={60}
      >
        <CheckoutView {...checkoutProps} />
      </BottomSheet>
      <MiniCatalogSheet
        open={miniCatalogOpen}
        title={miniCatalogTitle}
        products={miniCatalogProducts}
        onClose={() => setMiniCatalogOpen(false)}
        onAction={handlers.handleAction}
      />

      <ProductSheet
        open={productOpen}
        onClose={() => {
          setProductOpen(false);
          setSheetProduct(null);
          setProductInitialTab("info");
          setProductInitialOpenComment(false);
          setProductOpenedFromCatalog(false);
        }}
        onBack={
          productOpenedFromCatalog
            ? () => {
              setProductOpen(false);
              setSheetProduct(null);
              setProductInitialTab("info");
              setProductInitialOpenComment(false);
              setProductOpenedFromCatalog(false);
            }
            : null
        }
        showBackButton={productOpenedFromCatalog}
        product={sheetProduct}
        topOffset={60}
        onAddToCart={handlers.handleProductAddToCart}
        onOpenProduct={handlers.handleOpenProduct}
        onToggleFavorite={handlers.handleToggleFavorite}
        isFavorite={isFavorite}
        initialTab={productInitialTab}
        initialOpenComment={productInitialOpenComment}
      />

      {showOnboarding && (
        <Onboarding onFinish={() => setShowOnboarding(false)} />
      )}
      {beerFinderOpen && (
        <BottomSheet
          open={beerFinderOpen}
          onClose={() => setBeerFinderOpen(false)}
          title="Beer Finder"
          topOffset={60}
        >
          <div className="beerFinderWrapper">
          <BeerFinder
            mode={beerFinderMode}
            savedResult={beerFinderSaved}
            isLoggedIn={isLoggedIn}
            onLogin={async () => {
              try {
                await loginWithGoogle();
              } catch (e) {
                console.error("beer finder login error:", e);
              }
            }}
            onOpenProduct={(product) => {
              if (!product) return;

              setBeerFinderProduct(product);
              setBeerFinderProductOpen(true);
            }}
            onShowRecommendations={async ({ answers, profile }) => {
              const beerQuizPayload = mapBeerQuizToFilters(answers, profile);

              const res = await apiBeerFinder(beerQuizPayload);

              const latest = await apiGetMyBeerFinder();
              setBeerFinderSaved(latest?.hasProfile ? latest : null);

              return res;
            }}
          />
          </div>
        </BottomSheet>
      )}

      <ProductSheet
        open={beerFinderProductOpen}
        onClose={() => {
          setBeerFinderProductOpen(false);
          setBeerFinderProduct(null);
        }}
        product={beerFinderProduct || {}}
        topOffset={60}
        onAddToCart={handlers.handleProductAddToCart}
        onOpenProduct={(p) => {
          if (!p) return;

          setBeerFinderProduct(p);
          setBeerFinderProductOpen(true);
        }}
        onToggleFavorite={handlers.handleToggleFavorite}
        isFavorite={
          beerFinderProduct
            ? isFavoriteProductByIds(beerFinderProduct, favoriteIds)
            : false
        }
      />
    </>
  );
}